import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ImagePlus, Sparkles, Loader2 } from "lucide-react";

const R = {
  en: { illustrate: "Illustrate session", all: "Illustrate all sessions", generating: "Drawing…", done: "Illustration ready", model: "Nano Banana" },
  fr: { illustrate: "Illustrer la séance", all: "Illustrer toutes les séances", generating: "Dessin en cours…", done: "Illustration prête", model: "Nano Banana" },
};

export function useProgramImages(programId, open) {
  const [images, setImages] = useState({});
  const [busy, setBusy] = useState({});
  useEffect(() => {
    if (!open) return;
    api.get(`/programs/${programId}/images`).then((r) => {
      setImages(Object.fromEntries(r.data.map((i) => [i.day_index, i.image_data_url])));
    }).catch(() => {});
  }, [programId, open]);

  const illustrate = async (dayIndex) => {
    if (busy[dayIndex] || images[dayIndex]) return;
    setBusy((b) => ({ ...b, [dayIndex]: true }));
    try {
      const { data } = await api.post(`/programs/${programId}/days/${dayIndex}/image`);
      setImages((m) => ({ ...m, [dayIndex]: data.image_data_url }));
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(e?.response?.data?.detail || "Illustration failed");
    } finally {
      setBusy((b) => ({ ...b, [dayIndex]: false }));
    }
  };
  return { images, busy, illustrate };
}

export function IllustrateAllButton({ days, images, busy, illustrate, programId }) {
  const { lang } = useLang();
  const r = R[lang] || R.en;
  const missing = days.map((_, i) => i).filter((i) => !images[i]);
  const running = Object.values(busy).some(Boolean);
  if (!missing.length) return null;
  const run = async () => { for (const i of missing) await illustrate(i); };
  return (
    <Button type="button" variant="outline" size="sm" onClick={run} disabled={running} data-testid={`program-illustrate-all-${programId}`} className="border-[#1E2638] bg-transparent text-emerald-400 hover:bg-[#090C10]">
      {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
      {running ? r.generating : `${r.all} (${missing.length})`}
    </Button>
  );
}

export function ProgramDayImage({ programId, dayIndex, image, busy, onIllustrate }) {
  const { lang } = useLang();
  const r = R[lang] || R.en;
  if (image) {
    return (
      <div className="relative mt-2 overflow-hidden rounded-lg border border-[#1E2638] aspect-video" data-testid={`program-day-image-${programId}-${dayIndex}`}>
        <img src={image} alt="" className="w-full h-full object-cover" />
        <span className="absolute bottom-2 right-2 text-[9px] font-mono-tech tracking-wider bg-black/60 text-emerald-300 px-2 py-0.5 rounded">{r.model}</span>
      </div>
    );
  }
  return (
    <button type="button" onClick={onIllustrate} disabled={busy} data-testid={`program-day-illustrate-${programId}-${dayIndex}`}
      className="mt-2 w-full aspect-[16/5] rounded-lg border border-dashed border-[#1E2638] text-xs font-mono-tech tracking-wider text-slate-500 hover:text-emerald-400 hover:border-emerald-400/50 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
      {busy ? r.generating : r.illustrate}
    </button>
  );
}
